import * as Styled from './PunchupSendTickets.styles';

import { checkTicketsAvailability, getEvent, sendTickets } from './services';
import { handleSubmit, initialFormData, validationSchema } from './utils';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { IdParamsType } from 'utils/types';
import { PunchupShowDashboardPage } from 'features/common/components/Punchup/ShowDashboardPage/PunchupShowDashboardPage';
import { NestedTicketObject } from './types';
import { PunchupErrorModal } from './components/PunchupErrorModal/PunchupErrorModal';
import { PunchupSendTicketsConfirmation } from './components/PunchupSendTicketsConfirmation';
import { PunchupSendTicketsForm } from './components/PunchupSendTicketsForm';
import { ROUTES } from 'features/routes';
import { useForm } from 'react-hook-form';
import { useRef, useState } from 'react';
import { useToastNotification } from 'hooks/useToastNotification';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

export const PunchupSendTickets = () => {
	const { t } = useTranslation();
	const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
	// One send at a time (TIC-719): a second click while the first send is still in flight
	// created a second batch of comps. The ref blocks a click in the same render; the state
	// disables the Send button until the send settles.
	const sendInFlight = useRef(false);
	const [isSending, setIsSending] = useState(false);
	const releaseSend = () => {
		sendInFlight.current = false;
		setIsSending(false);
	};

	const { showNotification, TYPES } = useToastNotification();
	const { push } = useHistory();
	const formMethods = useForm({
		defaultValues: initialFormData,
		resolver: yupResolver(validationSchema(t)),
	});
	const { id } = useParams<IdParamsType>();

	const { data: event, refetch } = useQuery({
		queryKey: ['event', 'promoCodes', id],
		queryFn: () => getEvent(id),
		enabled: !!id,
	});

	const closeModal = () => {
		setIsErrorModalOpen(false);
		refetch();
	};

	const { mutate: mutateCheckAvailability } = useMutation({
		mutationFn: async (tickets: NestedTicketObject) => {
			try {
				const data = await checkTicketsAvailability(tickets);
				mutateSendTickets(formMethods.getValues());
				return data;
			} catch {
				setIsErrorModalOpen(true);
				releaseSend();
			}
		},
	});

	const { mutate: mutateSendTickets } = useMutation({
		mutationFn: sendTickets,
		onSuccess: () => {
			push(ROUTES.COMPED_TICKETS.replace(':id', id));
			showNotification({
				message: t('send-tickets-success'),
				type: TYPES.SUCCESS,
			});
		},
		onError: () =>
			showNotification({
				message: t('send-tickets-error'),
				type: TYPES.ERROR,
			}),
		onSettled: releaseSend,
	});

	return (
		<PunchupShowDashboardPage pageName="Send Tickets">
			<Styled.FormContainer
				onSubmit={(data) => {
					if (sendInFlight.current) return;
					sendInFlight.current = true;
					setIsSending(true);
					if (event?.seatsEventKey) {
						mutateSendTickets(data);
					} else {
						handleSubmit(data, mutateCheckAvailability);
					}
				}}
				formMethods={formMethods}
			>
				<PunchupSendTicketsForm event={event} />
				<PunchupSendTicketsConfirmation isSending={isSending} />
			</Styled.FormContainer>

			{isErrorModalOpen && <PunchupErrorModal closeModal={closeModal} />}
		</PunchupShowDashboardPage>
	);
};
